/**
 * In-App Browser Detection Utility
 *
 * Detects when the app is running inside social media app browsers
 * (Facebook, Instagram, TikTok, etc.) which have limited functionality.
 *
 * These browsers often have issues with:
 * - Payment flows (3DS challenges)
 * - OAuth popup-based authentication
 * - Geolocation verification
 * - PWA install prompts
 */

import { apiClient } from './api-client';

export type InAppBrowserName =
  | 'Facebook'
  | 'Instagram'
  | 'TikTok'
  | 'Twitter'
  | 'LinkedIn'
  | 'Snapchat'
  | 'Pinterest'
  | 'WeChat'
  | 'Line'
  | 'Unknown';

export type Platform = 'ios' | 'android' | 'unknown';

export interface InAppBrowserInfo {
  isInAppBrowser: boolean;
  browserName: InAppBrowserName | null;
  platform: Platform;
}

const IN_APP_BROWSER_PATTERNS: Record<InAppBrowserName, RegExp> = {
  Facebook: /FBAN|FBAV|FB_IAB|FBIOS|FBSS/i,
  Instagram: /Instagram/i,
  TikTok: /BytedanceWebview|TikTok|musical_ly/i,
  Twitter: /Twitter/i,
  LinkedIn: /LinkedInApp/i,
  Snapchat: /Snapchat/i,
  Pinterest: /Pinterest/i,
  WeChat: /MicroMessenger/i,
  Line: /\bLine\b/i,
  Unknown: /WebView|wv\)/i, // Generic WebView detection
};

/**
 * Detect if current browser is an in-app browser
 */
export function detectInAppBrowser(): InAppBrowserInfo {
  const ua =
    navigator.userAgent || navigator.vendor || (window as any).opera || '';

  // Check for known in-app browsers
  let browserName: InAppBrowserName | null = null;

  for (const [name, pattern] of Object.entries(IN_APP_BROWSER_PATTERNS)) {
    if (pattern.test(ua)) {
      browserName = name as InAppBrowserName;
      // Prefer specific matches over "Unknown"
      if (name !== 'Unknown') break;
    }
  }

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isAndroid = /Android/.test(ua);

  return {
    isInAppBrowser: browserName !== null,
    browserName,
    platform: isIOS ? 'ios' : isAndroid ? 'android' : 'unknown',
  };
}

/**
 * Get the current URL suitable for copying/sharing
 * Preserves important query params but removes any sensitive tokens
 */
export function getShareableUrl(): string {
  const url = new URL(window.location.href);
  // Remove any sensitive params if they exist
  url.searchParams.delete('token');
  url.searchParams.delete('session');
  return url.toString();
}

/**
 * Attempt to open URL in external browser (Android only)
 * Returns true if redirect was attempted, false if not supported
 */
export function openInExternalBrowser(url?: string): boolean {
  const { platform } = detectInAppBrowser();
  const targetUrl = url || getShareableUrl();

  if (platform !== 'android') {
    return false;
  }

  try {
    // Remove protocol for intent URL
    const urlWithoutProtocol = targetUrl.replace(/^https?:\/\//, '');

    // Generic Android intent - opens in user's default browser
    const intentUrl = `intent://${urlWithoutProtocol}#Intent;scheme=https;action=android.intent.action.VIEW;end`;

    window.location.href = intentUrl;
    return true;
  } catch {
    return false;
  }
}

/**
 * Get display name for the detected browser
 */
export function getBrowserDisplayName(
  browserName: InAppBrowserName | null
): string {
  if (!browserName) return 'this app';
  if (browserName === 'Unknown') return 'this app';
  return browserName;
}

/**
 * Transfer token expiry time in seconds (must match backend)
 */
export const TRANSFER_TOKEN_EXPIRY_SECONDS = 180;

/**
 * Generate a transfer token and return URL with token appended.
 * The token allows seamless session transfer to external browser.
 *
 * @returns Object containing the URL with token and expiry time
 * @throws Error if token generation fails
 */
export async function generateTransferUrl(): Promise<{
  url: string;
  expiresIn: number;
}> {
  // Generate transfer token from backend
  const response = await apiClient.post<{ token: string; expiresIn: number }>(
    '/auth/transfer-token'
  );

  // Build URL with token
  const url = new URL(window.location.href);
  // Remove any existing transfer token or sensitive params
  url.searchParams.delete('_t');
  url.searchParams.delete('token');
  url.searchParams.delete('session');
  // Add new token
  url.searchParams.set('_t', response.token);

  return {
    url: url.toString(),
    expiresIn: response.expiresIn,
  };
}
